import { useLazyQuery, useMutation } from '@apollo/client'
import ButtonBase from 'components/partials/button'
import DropdownBase, { Option } from 'components/partials/dropDown'
// import InputBase from 'components/partials/inputBase'
import IconInformation from 'assets/images/icon-information.svg'
import { TabProps } from 'components/partials/tabs'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import {
  ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG,
  AgencyGroupJobTypeEnum,
  DELETE_CONFIG_AGENCY_GROUP_BY_JOB_TYPE,
  GET_ALL_AGENCY_GROUP,
  GET_DETAIL_AGENCY_GROUP_BY_ID,
  IAgencyGroupJobConfigInput,
} from 'queries/admin/agencyGroup'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { IAgencyGroup, IAgencyGroupJobConfig, IForwardEmailConfig } from '../interface'
import EmailTemplate from './components/emailTemplate'
import MultiEmailInput from './components/multiEmailInput'
import { AGENCY_GROUP } from 'constants/common'
import InputBase from 'components/partials/inputBase'
import ConfirmDeleteDialog from '../dialogs/confirmDeleteDialog'
import { validateGoogleDocUrl } from 'utils/regex'
import { useSession } from 'helpers/checkSession'
import AgencyActivityTable from './components/activity-table'

interface Props extends TabProps {
  dataInput: {
    currentAgencyGroup: IAgencyGroup
  }
}

const CUSTOM_JOB_TYPE = AgencyGroupJobTypeEnum.FORWARD_EMAIL

interface IDropdownAgencyGroupOption extends Option {}

const ForwardEmail: React.FC<Props> = ({ dataInput }) => {
  const { handleErrorSession } = useSession()
  const [currentAgencyGroup, setCurrentAgencyGroup] = useState(dataInput.currentAgencyGroup)
  const [forwardEmailConfig, setForwardEmailConfig] = useState<IAgencyGroupJobConfig>()
  const [originalData, setOriginalData] = useState<IForwardEmailConfig>()

  const [isEditMode, setIsEditMode] = useState(false)
  const [isOpenDialogConfirmDelete, setIsOpenDialogConfirmDelete] = useState(false)
  const [dropdownAgencyGroupOptions, setDropdownAgencyGroupOptions] = useState<
    IDropdownAgencyGroupOption[]
  >([])

  const [getAllAgencyGroupQuery] = useLazyQuery(GET_ALL_AGENCY_GROUP, {
    variables: {
      skip: 0,
      take: 50,
    },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data) return

      const options: IDropdownAgencyGroupOption[] = []

      for (const agencyGroup of data.getAllAgencyGroup) {
        options.push({ id: agencyGroup.agencyGroupId, name: agencyGroup.agencyGroupName })
      }

      setDropdownAgencyGroupOptions(options)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })
  const [addOrEditAgencyGroupJobConfig] = useMutation(ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG)
  const [deleteConfigAgencyGroupByJobType] = useMutation(DELETE_CONFIG_AGENCY_GROUP_BY_JOB_TYPE)

  const [getDetailAgencyGroupByIdQuery, { refetch: refetchDetailData }] = useLazyQuery(
    GET_DETAIL_AGENCY_GROUP_BY_ID,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const config = data?.getDetailAgencyGroupById?.[0]
        setForwardEmailConfig(config)
        setOriginalData({
          sendingEmailSubject: config?.forwardEmail?.sendingEmailSubject ?? '',
          cc: config?.forwardEmail?.cc ?? [],
          to: config?.forwardEmail?.to ?? [],
          htmlTemplate: config?.forwardEmail?.htmlTemplate ?? {},
        })
      },
      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    },
  )

  const {
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    control,
    formState: { isDirty },
  } = useForm<IForwardEmailConfig>({
    defaultValues: {
      sendingEmailSubject: '',
      to: [],
      cc: [],
      htmlTemplate: {
        htmlString: '',
        templateType: AGENCY_GROUP.EMAIL_TEMPLATE_TYPE.DOCUMENT_URL,
        documentURL: '',
      },
    },
  })

  const handleChangeEditMode = (isEdit: boolean) => {
    setIsEditMode(isEdit)
  }

  const onSelectAgencyGroupOption = (option: IDropdownAgencyGroupOption) => {
    if (currentAgencyGroup?.agencyGroupId === option.id) return

    if (typeof option.id !== 'number') return

    const agencyGroup: IAgencyGroup = {
      agencyGroupId: option.id,
      agencyGroupName: option.name,
      agencies: [],
      agencyGroupJobConfigs: [],
    }

    setCurrentAgencyGroup(agencyGroup)
    handleChangeEditMode(false)
  }

  const isInvalidTemplate = (data: IForwardEmailConfig) => {
    const { templateType, documentURL, htmlString } = data.htmlTemplate
    const { DOCUMENT_URL, HTML_CSS } = AGENCY_GROUP.EMAIL_TEMPLATE_TYPE
    let hasError = false

    switch (templateType) {
      case DOCUMENT_URL:
        if (documentURL.length === 0) {
          setError('htmlTemplate', {
            message: 'Document URL is required',
          })
          hasError = true
        }

        if (!validateGoogleDocUrl(documentURL)) {
          setError('htmlTemplate', {
            message:
              'Invalid Document URL. Example: https://docs.google.com/document/d/1x67hRbDWU5UNIlfxiPrMlOY-IR3pE3ENYIrKlU_xOYw',
          })
          hasError = true
        }
        break
      case HTML_CSS:
        if (htmlString.length === 0) {
          setError('htmlTemplate', {
            message: 'Missing Template. Please try again',
          })
          hasError = true
        }
        break
      default:
        break
    }

    if (!hasError) {
      clearErrors('htmlTemplate')
    }

    return hasError
  }

  const isInvalidFormData = (data: IForwardEmailConfig) => {
    let hasError = false

    if (data.sendingEmailSubject.trim().length === 0) {
      setError('sendingEmailSubject', {
        message: 'Sending Email Subject is required',
      })
      hasError = true
    } else {
      clearErrors('sendingEmailSubject')
    }

    if (data.to.length === 0) {
      setError('to', {
        message: 'To Email is required',
      })
      hasError = true
    } else {
      clearErrors('to')
    }

    if (isInvalidTemplate(data)) {
      hasError = true
    }

    return hasError
  }

  const clearErrorTemplate = () => {
    clearErrors('htmlTemplate')
  }

  const handleSave = () => {
    const data = getValues()

    if (isInvalidFormData(data)) return

    const customConfig: IAgencyGroupJobConfigInput = {
      emailTemplateURL: null,
      htmlTemplateString: null,
      subEmailManagementConfigId: null,
      customEmailType: null,
      customJobType: CUSTOM_JOB_TYPE,
      forwardWebhook: null,
      forwardEmail: data,
      forwardFormatTemplate: null,
    }

    addOrEditAgencyGroupJobConfig({
      variables: {
        data: {
          agencyGroupId: currentAgencyGroup.agencyGroupId,
          customJobType: CUSTOM_JOB_TYPE,
          customConfigs: [customConfig],
        },
      },
      onCompleted: () => {
        handleChangeEditMode(false)
        refetchDetailData()
        notify('Email configuration updated successfully!', STATUS.SUCCESS)
      },
      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    })
  }

  const handleCancel = () => {
    reset(originalData)
    setIsEditMode(false)
  }

  const onClickRemoveBtn = () => {
    setIsOpenDialogConfirmDelete(true)
  }

  const handleCloseConfirmDialog = () => {
    setIsOpenDialogConfirmDelete(false)
  }

  const handleDeleteForwardEmailConfig = () => {
    deleteConfigAgencyGroupByJobType({
      variables: {
        agencyGroupId: currentAgencyGroup.agencyGroupId,
        customJobType: CUSTOM_JOB_TYPE,
      },
      onCompleted: (data) => {
        if (data.deleteConfigAgencyGroupByJobType) {
          notify('Remove successfully', STATUS.SUCCESS)
        } else {
          notify('Something wrong, please try again', STATUS.ERROR)
        }
        refetchDetailData()
        setIsOpenDialogConfirmDelete(false)
      },

      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    })
  }

  useEffect(() => {
    getAllAgencyGroupQuery()
  }, [])

  useEffect(() => {
    if (currentAgencyGroup?.agencyGroupId) {
      getDetailAgencyGroupByIdQuery({
        variables: {
          agencyGroupId: currentAgencyGroup.agencyGroupId,
          customJobType: CUSTOM_JOB_TYPE,
        },
      })
    }
  }, [reset, currentAgencyGroup?.agencyGroupId])

  useEffect(() => {
    if (forwardEmailConfig?.forwardEmail) {
      setValue('to', forwardEmailConfig.forwardEmail.to)
      setValue('cc', forwardEmailConfig.forwardEmail.cc)
      setValue('sendingEmailSubject', forwardEmailConfig.forwardEmail.sendingEmailSubject)
      setValue('htmlTemplate', forwardEmailConfig.forwardEmail.htmlTemplate)
    } else {
      reset()
    }
  }, [forwardEmailConfig])

  return (
    <div className="px-20 md:px-10 space-y-2">
      <div className="pb-5 flex">
        <img className="h-5" src={IconInformation} alt="Icon-Information" />
        <span className="italic text-sm">Config forward email</span>
      </div>
      <div className="w-full">
        <span>
          <strong>Agency Group</strong>
        </span>
        <div className="flex w-full space-x-2">
          <DropdownBase
            className="w-full"
            options={dropdownAgencyGroupOptions}
            initialValue={currentAgencyGroup?.agencyGroupName ?? ''}
            handleOptions={(option) =>
              onSelectAgencyGroupOption(option as IDropdownAgencyGroupOption)
            }
            placeHolder="Select agency group to add or edit"
          />
        </div>
      </div>

      {!currentAgencyGroup ? null : !isEditMode ? (
        <div className="flex w-full justify-end space-x-2">
          {forwardEmailConfig && (
            <ButtonBase
              className="bg-secondary-2 hover:bg-secondary-bold2"
              onClick={onClickRemoveBtn}
            >
              Remove
            </ButtonBase>
          )}
          <ButtonBase
            onClick={() => {
              setIsEditMode(true)
            }}
          >
            {!forwardEmailConfig ? 'Add' : 'Edit'}
          </ButtonBase>
        </div>
      ) : (
        <div className="w-full">
          <div className="flex justify-end space-x-1 overflow-hidden">
            <ButtonBase bgDisabled="bg-primary-shade3" disabled={!isDirty} onClick={handleSave}>
              Save
            </ButtonBase>
            <ButtonBase onClick={handleCancel}>Cancel</ButtonBase>
          </div>
        </div>
      )}

      {(forwardEmailConfig || isEditMode) && (
        <div className="w-full pt-5">
          <form>
            <div>
              <label>
                <strong>Subject:</strong>
              </label>
              <Controller
                name="sendingEmailSubject"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <InputBase className="w-full" disable={!isEditMode} {...field} />
                    {fieldState.error && (
                      <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                    )}
                  </div>
                )}
              />
            </div>
            <div>
              <label>
                <strong>Email To:</strong>
              </label>
              <Controller
                name="to"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <MultiEmailInput
                      className="flex flex-wrap h-auto w-full gap-2"
                      value={field.value}
                      onChange={field.onChange}
                      isEditing={isEditMode}
                    />
                    {fieldState.error && (
                      <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                    )}
                  </div>
                )}
              />
            </div>
            <div className="pt-5">
              <label>
                <strong>Email CC:</strong>
              </label>
              <Controller
                name="cc"
                control={control}
                render={({ field }) => (
                  <MultiEmailInput
                    className="flex flex-wrap h-auto w-full gap-2"
                    value={field.value}
                    onChange={field.onChange}
                    isEditing={isEditMode}
                  />
                )}
              />
            </div>
            <div className="py-5">
              <label>
                <strong>Template: </strong>
              </label>
              <Controller
                name="htmlTemplate"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <EmailTemplate
                      onChange={field.onChange}
                      htmlTemplate={field.value}
                      isEditMode={isEditMode}
                      sendingEmailSubject={getValues('sendingEmailSubject')}
                      clearErrorTemplate={clearErrorTemplate}
                    />
                    {fieldState.error && (
                      <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                    )}
                  </div>
                )}
              />
            </div>
          </form>
        </div>
      )}

      {currentAgencyGroup && (
        <div className="py-10">
          <span>
            <strong>Activity Log</strong>
          </span>
          <AgencyActivityTable
            activityType={'FORWARD_EMAIL'}
            currentAgencyGroup={currentAgencyGroup}
            handleFetchMore={() => {}}
            handleRefetch={() => {}}
            hasMoreData={false}
            isLoading={false}
          />
        </div>
      )}

      {isOpenDialogConfirmDelete && (
        <ConfirmDeleteDialog
          title="Confirm to delete email forward config?"
          handleCloseBtn={handleCloseConfirmDialog}
          handleConfirmBtn={handleDeleteForwardEmailConfig}
          isOpenDialog={isOpenDialogConfirmDelete}
          dialogStyle={{ width: '450px', maxHeight: '200px' }}
        />
      )}
    </div>
  )
}

export default ForwardEmail
